import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getAdminCompanies, createCompany, updateCompany, deleteCompany } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Search, Building2, Eye, EyeOff } from 'lucide-react';
import AdminSidebar from '@/components/AdminSidebar';

const categories = ['General', 'Alimentación', 'Tecnología', 'Renovables', 'Servicios', 'Comercio', 'Construcción', 'Hostelería', 'Salud', 'Salud y Belleza', 'Ocio', 'Transporte', 'Vinos', 'Eventos', 'Consultoría', 'Inmobiliaria', 'Finanzas', 'Industria', 'Multisector', 'Otros Servicios'];

const AdminCompanies = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo_url: '',
    address: '',
    phone: '',
    hours: '',
    website: '',
    category: 'General',
    access_code: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadCompanies();
  }, [isAuthenticated, navigate]);

  const loadCompanies = async () => {
    try {
      const response = await getAdminCompanies();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (company = null) => {
    if (company) {
      setSelectedCompany(company);
      setFormData({
        name: company.name || '',
        description: company.description || '',
        logo_url: company.logo_url || '',
        address: company.address || '',
        phone: company.phone || '',
        hours: company.hours || '',
        website: company.website || '',
        category: company.category || 'General',
        access_code: company.access_code || ''
      });
    } else {
      setSelectedCompany(null);
      setFormData({
        name: '',
        description: '',
        logo_url: '',
        address: '',
        phone: '',
        hours: '',
        website: '',
        category: 'General',
        access_code: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedCompany) {
        await updateCompany(selectedCompany.id, formData);
      } else {
        await createCompany(formData);
      }
      setShowModal(false);
      loadCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCompany(selectedCompany.id);
      setShowDeleteDialog(false);
      setSelectedCompany(null);
      loadCompanies();
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.category?.toLowerCase().includes(search.toLowerCase())
  );

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-[240px_1fr]">
      <AdminSidebar />
      
      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="companies-title">
                Gestión de Empresas
              </h1>
              <p className="text-[#64748B] mt-1">
                {companies.length} empresas registradas
              </p>
            </div>
            <Button onClick={() => handleOpenModal()} className="btn-primary" data-testid="add-company-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Empresa
            </Button>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar empresas..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-companies"
            />
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="spinner"></div>
              </div>
            ) : filteredCompanies.length === 0 ? (
              <div className="text-center py-20">
                <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-[#64748B]">No se encontraron empresas</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Logo</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Código Acceso</TableHead>
                    <TableHead>Teléfono</TableHead>
                    <TableHead className="w-20">Visible</TableHead>
                    <TableHead className="w-32">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompanies.map((company) => (
                    <TableRow key={company.id} data-testid={`company-row-${company.id}`}>
                      <TableCell>
                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex items-center justify-center">
                          {company.logo_url ? (
                            <img 
                              src={company.logo_url} 
                              alt={company.name}
                              className="w-full h-full object-contain"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = `https://placehold.co/80x80/f1f5f9/d1223b?text=${encodeURIComponent(company.name.substring(0, 2))}`;
                              }}
                            />
                          ) : (
                            <span className="text-xs font-bold text-[#d1223b]">{company.name.substring(0, 2)}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{company.name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{company.category || 'General'}</Badge>
                      </TableCell>
                      <TableCell>
                        {company.access_code ? (
                          <code className="bg-[#fcd605]/20 text-[#0F172A] px-2 py-1 rounded text-sm font-mono font-bold">{company.access_code}</code>
                        ) : '-'}
                      </TableCell>
                      <TableCell>{company.phone || '-'}</TableCell>
                      <TableCell>
                        <button
                          onClick={async () => {
                            await updateCompany(company.id, { is_visible: !company.is_visible });
                            loadCompanies();
                          }}
                          className={`p-1.5 rounded-lg transition-colors ${company.is_visible !== false ? 'text-emerald-600 hover:bg-emerald-50' : 'text-slate-400 hover:bg-slate-100'}`}
                          data-testid={`toggle-visibility-${company.id}`}
                          title={company.is_visible !== false ? 'Visible - click para ocultar' : 'Oculta - click para mostrar'}
                        >
                          {company.is_visible !== false ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                        </button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenModal(company)}
                            data-testid={`edit-company-${company.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedCompany(company);
                              setShowDeleteDialog(true);
                            }}
                            data-testid={`delete-company-${company.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </main>

      {/* Company Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              {selectedCompany ? 'Editar Empresa' : 'Nueva Empresa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="company-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="category">Categoría</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger data-testid="company-category-select">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="logo_url">URL del Logo</Label>
              <Input
                id="logo_url"
                value={formData.logo_url}
                onChange={(e) => setFormData({...formData, logo_url: e.target.value})}
                placeholder="https://..."
                data-testid="company-logo-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={3}
                data-testid="company-description-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Teléfono</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  data-testid="company-phone-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="hours">Horario</Label>
                <Input
                  id="hours"
                  value={formData.hours}
                  onChange={(e) => setFormData({...formData, hours: e.target.value})}
                  placeholder="Lun-Vie: 9:00-18:00"
                  data-testid="company-hours-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Dirección</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                data-testid="company-address-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Sitio Web</Label>
              <Input
                id="website"
                value={formData.website}
                onChange={(e) => setFormData({...formData, website: e.target.value})}
                placeholder="https://..."
                data-testid="company-website-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="access_code">Código de Acceso para Empleados</Label>
              <Input
                id="access_code"
                value={formData.access_code}
                onChange={(e) => setFormData({...formData, access_code: e.target.value.toUpperCase()})}
                placeholder="Ej: ABC2024"
                className="font-mono uppercase"
                data-testid="company-access-code-input"
              />
              <p className="text-xs text-[#64748B]">Este código lo usarán los empleados para acceder a la app</p>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-primary" data-testid="save-company-btn">
                {selectedCompany ? 'Guardar Cambios' : 'Crear Empresa'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar empresa?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se eliminará la empresa "{selectedCompany?.name}" y todos sus cupones asociados.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCompanies;
